import React from 'react';

// Minimal markdown renderer for problem prompts and hints.
// Supports: paragraphs (blank-line separated), single-line breaks,
// bullet lists (lines starting with •, -, *), inline `code`, **bold**, *italic*.
// Deliberately tiny — no headings, links, or HTML escape concerns since the
// content is authored by us and lives in JSON.

type InlineNode = React.ReactNode;

const INLINE_RE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\n]+\*)/g;

function renderInline(text: string): InlineNode[] {
  const out: InlineNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${m.index}`;
    if (tok.startsWith('**') && tok.endsWith('**')) {
      out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`') && tok.endsWith('`')) {
      out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('*') && tok.endsWith('*')) {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    } else {
      out.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function isBulletLine(line: string): boolean {
  return /^\s*([•\-*])\s+/.test(line);
}

function stripBullet(line: string): string {
  return line.replace(/^\s*[•\-*]\s+/, '');
}

// Render a single block: either a bulleted list or a paragraph.
function renderBlock(block: string, key: number): React.ReactNode {
  const lines = block.split('\n');
  const allBullets = lines.length > 0 && lines.every(l => l.trim() === '' || isBulletLine(l));
  if (allBullets && lines.some(isBulletLine)) {
    const items = lines.filter(isBulletLine).map(stripBullet);
    return (
      <ul key={key} className="md-list">
        {items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </ul>
    );
  }
  // Paragraph — preserve single-line breaks within.
  const nodes: React.ReactNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) nodes.push(<br key={`br-${i}`} />);
    nodes.push(<React.Fragment key={`l-${i}`}>{renderInline(line)}</React.Fragment>);
  });
  return <p key={key} className="md-p">{nodes}</p>;
}

export function Markdown({ text, className }: { text: string; className?: string }): React.ReactElement {
  const blocks = text.split(/\n{2,}/);
  return (
    <div className={className}>
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}
